import type { Column, ColumnPick, SlipShape } from './types.js'

/** Fisse solo nel sistema; gli altri eventi sono quelli su cui si contano gli ordini. */
function splitLegs(slip: SlipShape): { bankers: number[]; free: number[] } {
  const isBanker = (i: number) => slip.type === 'system' && slip.legs[i].banker === true
  const all = slip.legs.map((_, i) => i)
  return { bankers: all.filter(isBanker), free: all.filter((i) => !isBanker(i)) }
}

/** Ordini effettivi: singola e multipla sono un solo ordine pari a tutti gli eventi; nel sistema gli ordini validi, senza doppioni. */
export function slipOrders(slip: SlipShape): number[] {
  const { free } = splitLegs(slip)
  if (slip.type !== 'system') return [free.length]
  return [...new Set(slip.systemSizes ?? [])].filter((k) => Number.isInteger(k) && k >= 1 && k <= free.length).sort((a, b) => a - b)
}

function combinations<T>(items: readonly T[], k: number): T[][] {
  if (k === 0) return [[]]
  if (items.length < k) return []
  const [first, ...rest] = items
  return [...combinations(rest, k - 1).map((c) => [first, ...c]), ...combinations(rest, k)]
}

/** Numero di colonne senza generarle: per ogni ordine k, il prodotto e_k(dimensioni degli eventi liberi) × Π(dimensioni delle fisse).
 * e_k è il k-esimo polinomio simmetrico elementare, calcolato con DP in O(legs × k). */
export function countColumns(slip: SlipShape): number {
  const { bankers, free } = splitLegs(slip)
  const size = (i: number) => slip.legs[i].selections.length
  const fixed = bankers.reduce((product, i) => product * size(i), 1)

  const orders = slipOrders(slip)
  if (orders.length === 0) return 0

  const maxOrder = Math.max(...orders)
  const sizes = free.map(size)

  // e[k] = k-esimo polinomio simmetrico elementare di sizes
  // e[0] = 1 (prodotto vuoto)
  const e = Array(maxOrder + 1).fill(0)
  e[0] = 1

  for (const s of sizes) {
    // Aggiorna in ordine decrescente per evitare di usare valori già aggiornati
    for (let k = Math.min(maxOrder, e.length - 1); k >= 1; k--) {
      e[k] += e[k - 1] * s
    }
  }

  // Somma sui valori di k validi
  let total = 0
  for (const k of orders) {
    total += e[k] * fixed
  }

  return total
}

const cartesian = (lists: ColumnPick[][]): Column[] =>
  lists.reduce<Column[]>((columns, list) => columns.flatMap((column) => list.map((pick) => [...column, pick])), [[]])

/**
 * Tutte le colonne (spec 4): per ogni ordine k, ogni combinazione di k eventi non fissi più tutte le fisse, poi il
 * prodotto cartesiano degli esiti di ciascun evento. Gli esiti della stessa partita non stanno mai insieme.
 */
export function enumerateColumns(slip: SlipShape): Column[] {
  const { bankers, free } = splitLegs(slip)
  return slipOrders(slip).flatMap((k) =>
    combinations(free, k).flatMap((combo) => {
      const legs = [...bankers, ...combo].sort((a, b) => a - b)
      return cartesian(legs.map((leg) => slip.legs[leg].selections.map((_, selection) => ({ leg, selection }))))
    }),
  )
}
